import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const HOMEPAGE_LISTING_LIMIT = 3;

const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

const fetchLatestListingsPayloadCreator = ({ config }, thunkAPI) => {
  const { dispatch, extra: sdk } = thunkAPI;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      page: 1,
      perPage: HOMEPAGE_LISTING_LIMIT,
      include: ['author', 'author.profileImage', 'images'],
      'fields.listing': [
        'title',
        'description',
        'geolocation',
        'price',
        'deleted',
        'state',
        // availabilityPlan is needed so ListingCard can compute "Next slot"
        // in the listing's local timezone.
        'availabilityPlan',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
        'publicData.yearsOfExperience',
        'publicData.businessName',
        'publicData.location',
        'publicData.categoryLevel1',
        'publicData.categoryLevel2',
        'publicData.categoryLevel3',
        'publicData.about',
        'metadata.averageRating',
        'metadata.reviewCount',
      ],
      // `profile.metadata.lastSeenOnline` powers the green "Online" badge.
      // `profile.publicData.showOnlineStatus` is the user's opt-out toggle.
      'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.metadata.lastSeenOnline',
        'profile.publicData.showOnlineStatus',
      ],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.square-small',
        'variants.square-small2x',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return { listingIds: resultIds(response.data) };
    });
};

export const fetchLatestListings = createAsyncThunk(
  'HomePage/fetchLatestListings',
  fetchLatestListingsPayloadCreator
);

const homePageSlice = createSlice({
  name: 'HomePage',
  initialState: {
    listingIds: [],
    fetchInProgress: false,
    fetchError: null,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchLatestListings.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchLatestListings.fulfilled, (state, action) => {
        state.listingIds = action.payload.listingIds;
        state.fetchInProgress = false;
      })
      .addCase(fetchLatestListings.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.error;
      });
  },
});

export default homePageSlice.reducer;

export const loadData = (params, search, config) => dispatch => {
  return dispatch(fetchLatestListings({ config }));
};
